import React, {useState, useContext, useRef, useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import SearchInputComponent from '../search-bar/search-input'
import {AppContext} from '../../context/context'
import {OntarioLanguageToggle} from '@ongov/ontario-design-system-component-library-react'

import {
  HeaderContainer,
  StyledHeader,
  HeaderRow,
  LogoContainer,
  HeaderRightSideContainer,
  SearchTogglerButton,
  SearchCloseButtonContainer,
  SearchCloseButton,
  MenuButton,
  NavigationContainer,
  NavigationList,
  NavigationListItem,
  NavigationLink,
  Overlay,
  ShowForMediumUp,
  ShowForSmallOnly,
} from './styles'

const Header = () => {
  const {t} = useTranslation('common')
  const [navOpen, setNavOpen] = useState(false)
  const [searchBarOpen, setSearchBarOpen] = useState(false)
  const {state} = useContext(AppContext)

  const searchInput = useRef(null)
  const menuButton = useRef(null)
  const header = useRef(null)

  const navLinks = [...t('header.navigation.links', {returnObjects: true})]

  useEffect(() => {
    // Import NPM Search Autosuggest package
    import('@ongov/ontario-search')

    // when searchBarOpen is set to true, shift the focus to the search input
    if (searchBarOpen) {
      document
        .querySelector('#ontario-search-autosuggest #search-input-field')
        ?.focus()
    }
    // when anywhere outside of the menu & header is clicked, setNavOpen to false and close the menu
    document.addEventListener('click', event => {
      if (header.current && !header.current.contains(event.target)) {
        setNavOpen(false)
      }
    })
  }, [searchBarOpen, setNavOpen])

  const trapMenuFocus = () => {
    if (menuButton.current) {
      menuButton.current.focus()
    }
  }

  return (
    <>
      <HeaderContainer ref={header}>
        <StyledHeader>
          <HeaderRow searchOpen={searchBarOpen}>
            <LogoContainer searchOpen={searchBarOpen}>
              <a
                href={t('header.ontarioLink')}
                aria-label={t('header.logoAriaLabel')}
              >
                <ShowForMediumUp>
                  <img
                    src="./images/ontario-logo-desktop.svg"
                    alt={t('header.logoAltText')}
                    width="180"
                    height="46"
                  />
                </ShowForMediumUp>
                <ShowForSmallOnly>
                  <img
                    src="./images/ontario-logo-mobile.svg"
                    alt={t('header.logoAltText')}
                    width="45"
                    height="46"
                  />
                </ShowForSmallOnly>
              </a>
            </LogoContainer>
            {/* Inserting NPM package into the 'ontario-search-autosuggest' element on DOM load */}
            <div
              id="ontario-search-autosuggest"
              className="ontario-header__search-container ontario-columns ontario-small-10 ontario-medium-offset-3 ontario-medium-6 ontario-large-offset-0 ontario-large-6"
            >
              <SearchInputComponent
                resultTitleKey="title"
                resultLinkKey="nps_link"
                resultIdKey="id"
                inputRef={searchInput}
                searchBarOpen={searchBarOpen}
              />
            </div>
            <HeaderRightSideContainer searchOpen={searchBarOpen}>
              <OntarioLanguageToggle
                role="button"
                language={state.language}
                url={t('header.languageToggleLink')}
              />
              <SearchTogglerButton
                role="button"
                onClick={() => setSearchBarOpen(true)}
                aria-label={
                  searchBarOpen
                    ? t('search.closeSearchBar')
                    : t('search.openSearchBar')
                }
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <path d="M15.5 14h-.8l-.28-.27A6.47 6.47 0 0 0 16 9.5a6.5 6.5 0 1 0-13 0A6.5 6.5 0 0 0 9.5 16a6.47 6.47 0 0 0 4.23-1.57l.27.28v.8l5 5 1.5-1.5-5-5zm-6 0C7 14 5 12 5 9.5S7 5 9.5 5 14 7 14 9.5 12 14 9.5 14z" />
                </svg>
                <ShowForMediumUp>
                  <span>{t('search.search')}</span>
                </ShowForMediumUp>
              </SearchTogglerButton>

              <MenuButton
                aria-controls="ontario-header-navigation"
                aria-label={
                  navOpen ? t('header.closeMenu') : t('header.openMenu')
                }
                onClick={() => (navOpen ? setNavOpen(false) : setNavOpen(true))}
                ref={menuButton}
              >
                {navOpen ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M19 6.4L17.6 5 12 10.6 6.4 5 5 6.4l5.6 5.6L5 17.6 6.4 19l5.6-5.6 5.6 5.6 1.4-1.4-5.6-5.6L19 6.4z"
                      fill="#fff"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"
                      fill="#fff"
                    />
                  </svg>
                )}

                <span>{t('header.menuButtonText')}</span>
              </MenuButton>
            </HeaderRightSideContainer>
            <SearchCloseButtonContainer searchOpen={searchBarOpen}>
              <SearchCloseButton
                id="ontario-header-search-close"
                aria-label={t('search.closeSearchBar')}
                searchOpen={searchBarOpen}
                onClick={() => setSearchBarOpen(false)}
              >
                <span aria-hidden="true">{t('search.close')}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M19 6.4L17.6 5 12 10.6 6.4 5 5 6.4l5.6 5.6L5 17.6 6.4 19l5.6-5.6 5.6 5.6 1.4-1.4-5.6-5.6L19 6.4z"
                    fill="#fff"
                  />
                </svg>
              </SearchCloseButton>
            </SearchCloseButtonContainer>
          </HeaderRow>
        </StyledHeader>
        <NavigationContainer
          aria-hidden={!navOpen}
          id="ontario-header-navigation"
        >
          <NavigationList navOpen={navOpen}>
            <ul>
              {navLinks
                ? navLinks.map((link, index) => {
                    const lastLink = index + 1 === navLinks.length
                    return lastLink ? (
                      <NavigationListItem key={`navLink-${index}`}>
                        <NavigationLink href={link.url} onBlur={trapMenuFocus}>
                          {link.name}
                        </NavigationLink>
                      </NavigationListItem>
                    ) : (
                      <NavigationListItem key={`navLink-${index}`}>
                        <NavigationLink href={link.url}>
                          {link.name}
                        </NavigationLink>
                      </NavigationListItem>
                    )
                  })
                : null}
            </ul>
          </NavigationList>
        </NavigationContainer>
      </HeaderContainer>
      <Overlay navOpen={navOpen} />
    </>
  )
}

export default Header
